import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select
} from '@mui/material';
import {
  MoveToInbox as InboxIcon,
  Person as PersonIcon,
  AccountTree as PedigreeIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import DatabaseService from '../services/database';
import { getBirdListLabels } from '../utils/birdSearch';
import { findSharedPigeonConflicts } from '../utils/shareDuplicates';
import { useLicense } from '../context/LicenseContext';

// "Shared with me": the recipient's inbox of pedigrees other fanciers have sent
// them (web-only feature). Accepting imports the bird + its pedigree into the
// loft, asking how to resolve possible duplicates matched by ring numbers + year.
const SharedWithMe = () => {
  const navigate = useNavigate();
  const { state: licenseState } = useLicense();
  const readOnly = licenseState?.mode === 'readonly' || licenseState?.mode === 'unlicensed';

  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Preview / accept dialog: { share, package } once a row is opened.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [banner, setBanner] = useState(null); // { type, text }
  // incoming package id -> { existingId, action: 'keep' | 'replace' }
  const [conflictChoices, setConflictChoices] = useState({});

  const load = useCallback(async () => {
    try {
      const rows = await DatabaseService.getIncomingShares();
      setShares(rows);
      setLoadError('');
    } catch (e) {
      console.error('Failed to load shared birds:', e);
      setLoadError('Could not load your shared birds. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPreview = async (share) => {
    setPreview({ share, package: null, conflicts: [] });
    setConflictChoices({});
    setPreviewLoading(true);
    setActionError('');
    try {
      const [detail, existingBirds] = await Promise.all([
        DatabaseService.getShare(share.id),
        DatabaseService.getAllBirds(true)
      ]);
      const conflicts = findSharedPigeonConflicts(detail.package?.birds, existingBirds);
      const initialChoices = {};
      for (const conflict of conflicts) {
        initialChoices[conflict.incomingId] = {
          existingId: conflict.candidates.length === 1 ? conflict.candidates[0].id : '',
          action: ''
        };
      }
      setConflictChoices(initialChoices);
      setPreview({ share: detail.share || share, package: detail.package, conflicts });
    } catch (e) {
      setActionError(e.message || 'Could not open this shared pedigree.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (!busy) {
      setPreview(null);
      setConflictChoices({});
    }
  };

  const setConflictChoice = (incomingId, patch) => {
    setConflictChoices((current) => ({
      ...current,
      [incomingId]: { ...current[incomingId], ...patch }
    }));
  };

  const selectedExistingIds = (preview?.conflicts || [])
    .map((conflict) => conflictChoices[conflict.incomingId]?.existingId)
    .filter((id) => id !== '' && id !== undefined);
  const hasDuplicateSelection = new Set(selectedExistingIds.map(Number)).size !== selectedExistingIds.length;
  const conflictsResolved = !hasDuplicateSelection && (preview?.conflicts || []).every((conflict) => {
    const choice = conflictChoices[conflict.incomingId];
    return choice && choice.existingId !== '' && (choice.action === 'keep' || choice.action === 'replace');
  });

  const accept = async () => {
    if (!preview || !preview.package) return;
    setBusy(true);
    setActionError('');
    let result = null;
    try {
      result = await DatabaseService.acceptSharedBird(preview.package, conflictChoices);
    } catch (e) {
      // Trial-cap / read-only / other server messages are user-facing.
      setActionError(e.message || 'Could not add this pigeon. Please try again.');
      setBusy(false);
      return;
    }
    // The import is the authoritative action; clearing the inbox is best-effort
    // (mirrors the server's own best-effort R2/email cleanup). A failed delete
    // must NOT report the accept as failed — otherwise the user re-accepts and
    // ring-less ancestors would be duplicated.
    try {
      await DatabaseService.deleteShare(preview.share.id);
    } catch (e) {
      console.warn('Shared bird imported, but clearing it from the inbox failed:', e);
    }
    // An all-ring-matched package creates nothing (the recipient already owns
    // these birds) — say so instead of claiming an addition.
    const label = preview.share.root_name || preview.share.root_ring || 'the pigeon';
    const created = result ? result.imported : null;
    const updated = result ? result.updated : 0;
    setBanner({
      type: 'success',
      text: updated > 0 && created > 0
        ? `Added "${label}" and updated ${updated} possible duplicate${updated === 1 ? '' : 's'} with the incoming information.`
        : updated > 0
          ? `Updated "${label}" with the incoming pigeon information and ancestry.`
          : created === 0
            ? `Kept your existing information for "${label}". No duplicate pigeon was added.`
            : `Added "${label}" and its pedigree to your loft.`
    });
    setPreview(null);
    setConflictChoices({});
    window.dispatchEvent(new Event('ppm-shares-changed'));
    setBusy(false);
    await load();
  };

  const decline = async (share) => {
    if (!window.confirm(`Remove the pigeon shared by ${share.from_email || 'this fancier'} from your inbox? This can't be undone.`)) return;
    setBusy(true);
    setActionError('');
    try {
      await DatabaseService.deleteShare(share.id);
      setPreview(null);
      window.dispatchEvent(new Event('ppm-shares-changed'));
      await load();
    } catch (e) {
      setActionError(e.message || 'Could not remove this share. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const shareTitle = (share) => {
    const { displayName } = getBirdListLabels({ name: share.root_name, ring_number: share.root_ring });
    return displayName;
  };

  const formatDate = (ms) => {
    const n = Number(ms);
    if (!Number.isFinite(n)) return '';
    try { return new Date(n).toLocaleDateString(); } catch (e) { return ''; }
  };

  const birdSummary = (bird) => {
    const { displayName, ringLabel } = getBirdListLabels(bird || {});
    return [displayName, ringLabel, bird?.year ? `Year ${bird.year}` : null]
      .filter(Boolean)
      .join(' • ');
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <InboxIcon color="primary" />
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.35rem', sm: '1.7rem' } }}>
          Shared with me
        </Typography>
      </Box>

      {banner && (
        <Alert severity={banner.type} sx={{ mb: 2 }} onClose={() => setBanner(null)}>
          {banner.text}
        </Alert>
      )}
      {loadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError('')}>{loadError}</Alert>}
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your license is inactive, so imports are paused. Received pedigrees stay here safely —
          restore your license to add them to your loft.
        </Alert>
      )}

      {shares.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <InboxIcon color="disabled" sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="h6" gutterBottom>No shared birds yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
              When another fancier shares a pigeon with your account's email, it appears here with its
              full pedigree and photos — ready to add to your loft.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {shares.map((share) => (
            <Card key={share.id} variant="outlined">
              <CardActionArea onClick={() => openPreview(share)}>
                <CardContent sx={{ py: 1.75 }}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <Box minWidth={0}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
                        {shareTitle(share)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mt={0.25} color="text.secondary">
                        <PersonIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          from {share.from_email || 'a fancier'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                      <Chip size="small" icon={<PedigreeIcon />} label={`${share.bird_count} bird${share.bird_count === 1 ? '' : 's'}`} />
                      {share.photo_count > 0 && (
                        <Chip size="small" icon={<PhotoIcon />} label={`${share.photo_count} photo${share.photo_count === 1 ? '' : 's'}`} />
                      )}
                      {formatDate(share.created_at) && (
                        <Typography variant="caption" color="text.secondary">{formatDate(share.created_at)}</Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}

      {/* Preview + accept/decline */}
      <Dialog
        open={Boolean(preview)}
        onClose={closePreview}
        maxWidth={preview?.conflicts?.length ? 'md' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          {preview?.conflicts?.length ? 'Review possible duplicates' : (preview ? shareTitle(preview.share) : '')}
        </DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          {previewLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
          ) : preview && (
            <>
              <DialogContentText component="div" sx={{ mb: 1 }}>
                Shared by <b>{preview.share.from_email || 'a fancier'}</b>
                {formatDate(preview.share.created_at) ? ` on ${formatDate(preview.share.created_at)}` : ''}.
              </DialogContentText>
              <Divider sx={{ my: 1.5 }} />
              <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                <Chip icon={<PedigreeIcon />} label={`${preview.share.bird_count} bird${preview.share.bird_count === 1 ? '' : 's'} in the pedigree`} />
                {preview.share.photo_count > 0 && (
                  <Chip icon={<PhotoIcon />} label={`${preview.share.photo_count} photo${preview.share.photo_count === 1 ? '' : 's'}`} />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Accepting adds this pigeon and its ancestors to your loft. Possible duplicates are
                matched by ring number and year; ring letters are ignored.
              </Typography>
              {preview.conflicts?.length > 0 && (
                <Box mt={2}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {preview.conflicts.length} possible duplicate{preview.conflicts.length === 1 ? '' : 's'} found.
                    Choose what to do with each pigeon before adding the pedigree.
                  </Alert>
                  {hasDuplicateSelection && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Two incoming pigeons cannot replace or link to the same pigeon in your loft.
                      Select a different local match for one of them.
                    </Alert>
                  )}
                  <Box display="flex" flexDirection="column" gap={2}>
                    {preview.conflicts.map((conflict) => {
                      const choice = conflictChoices[conflict.incomingId] || {};
                      const selected = conflict.candidates.find((bird) => bird.id === Number(choice.existingId));
                      return (
                        <Card key={conflict.incomingId} variant="outlined">
                          <CardContent>
                            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={1}>
                              <Chip
                                size="small"
                                color="warning"
                                label={conflict.incomingId === preview.package.root_id ? 'Shared pigeon' : 'Ancestor'}
                              />
                              <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                                Incoming: {birdSummary(conflict.incoming)}
                              </Typography>
                            </Box>

                            {conflict.candidates.length > 1 ? (
                              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <FormLabel id={`match-${conflict.incomingId}-label`} sx={{ mb: 0.75 }}>
                                  Which pigeon in your loft is the match?
                                </FormLabel>
                                <Select
                                  aria-labelledby={`match-${conflict.incomingId}-label`}
                                  value={choice.existingId ?? ''}
                                  displayEmpty
                                  onChange={(e) => setConflictChoice(conflict.incomingId, { existingId: e.target.value })}
                                >
                                  <MenuItem value="" disabled>Select a pigeon</MenuItem>
                                  {conflict.candidates.map((candidate) => (
                                    <MenuItem key={candidate.id} value={candidate.id}>
                                      {birdSummary(candidate)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, wordBreak: 'break-word' }}>
                                In your loft: {birdSummary(selected || conflict.candidates[0])}
                              </Typography>
                            )}

                            <FormControl component="fieldset" fullWidth disabled={choice.existingId === ''}>
                              <FormLabel id={`choice-${conflict.incomingId}-label`}>Information to keep</FormLabel>
                              <RadioGroup
                                aria-labelledby={`choice-${conflict.incomingId}-label`}
                                value={choice.action || ''}
                                onChange={(e) => setConflictChoice(conflict.incomingId, { action: e.target.value })}
                              >
                                <FormControlLabel
                                  value="keep"
                                  control={<Radio />}
                                  label={(
                                    <Box py={0.5}>
                                      <Typography variant="body2" fontWeight={700}>Keep my information</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Keep your details, photos, and ancestry. Incoming descendants will link to your pigeon.
                                      </Typography>
                                    </Box>
                                  )}
                                />
                                <FormControlLabel
                                  value="replace"
                                  control={<Radio />}
                                  label={(
                                    <Box py={0.5}>
                                      <Typography variant="body2" fontWeight={700}>Use the incoming information</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Replace details, photos, and ancestry. Your existing offspring links remain attached.
                                      </Typography>
                                    </Box>
                                  )}
                                />
                              </RadioGroup>
                            </FormControl>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap' }}>
          <Button color="error" onClick={() => preview && decline(preview.share)} disabled={busy || previewLoading} sx={{ mr: 'auto' }}>
            Decline
          </Button>
          <Button onClick={closePreview} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={accept}
            disabled={busy || previewLoading || readOnly || !preview?.package || !conflictsResolved}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {busy ? 'Adding…' : 'Add to my loft'}
          </Button>
        </DialogActions>
      </Dialog>

      {banner?.type === 'success' && shares.length === 0 && (
        <Box mt={2} textAlign="center">
          <Button variant="text" onClick={() => navigate('/birds')}>View my birds</Button>
        </Box>
      )}
    </Box>
  );
};

export default SharedWithMe;
